import type { CSSProperties } from "react";
import { createLayoutStore } from "../../cardResize/createLayoutStore";
import { cardGridStyleOf as cardSizeStyleOf } from "../../cardResize/cardResizeSpec";
import type { CardResizeSpec, CardSize } from "../../cardResize/cardResizeSpec";

// Размерите на екрана с масите в „Маркиране" (само десктоп, режим „Списък"):
// ширината на релсата вляво, картите на ред и височината на картата. Сменят се
// САМО с дръжките на екрана (`RailResizeHandle`, `CardResizeGrips`) и се пазят
// на КОМПЮТЪРА, като темата — localStorage["ordering_tables_layout:<ресторант>"]:
// размерите зависят от екрана, не от човека. BE не ги вижда.

export interface TablesLayout extends CardSize {
  /** Ширината на релсата в px; null — по подразбиране (`clamp(340px,26%,470px)`). */
  railWidth: number | null;
  /** Картите на ред; null — колкото се съберат по 150px (по подразбиране). */
  cardColumns: number | null;
  /** Височината на картата в px; null — по подразбиране (CARD_HEIGHT_DEFAULT).
   *  Точно толкова — съдържанието се събира в нея (`DesktopTableCard`). */
  cardHeight: number | null;
}

const TABLES_LAYOUT_DEFAULT: TablesLayout = {
  railWidth: null,
  cardColumns: null,
  cardHeight: null,
};

/** Най-тясната релса — търсачката с микрофона и една колона карти. */
export const RAIL_MIN_WIDTH = 180;
/** Най-тясната дясна част — колкото телефон в изгледа „като на телефон". */
export const DETAILS_MIN_WIDTH = 320;
/** Под тази ширина дясната част (поръчаното) е като на телефон — нищо не се
 *  крие, редовете са на няколко реда (`useDetailsPhoneLayout`). */
export const DETAILS_PHONE_LAYOUT_BELOW = 480;
/** Под тази ширина картата не се събира (името и сумата в компактен кегел). */
export const CARD_MIN_WIDTH = 84;
export const CARD_MAX_COLUMNS = 8;
/** Колкото беше картата преди дръжките: ред с името + ред със сумата (същото
 *  число е и резервът на `--card-height` в `cardGridClassOf`). */
const CARD_HEIGHT_DEFAULT = 70;
/** Един ред (името и сумата един до друг) в най-едрия кегел. */
export const CARD_HEIGHT_MIN = 44;
export const CARD_HEIGHT_MAX = 240;
/** Под тази височина двата реда не се събират — името и сумата са на един ред. */
export const CARD_ONE_LINE_BELOW = CARD_HEIGHT_DEFAULT;
/** Разстоянието между картите — `gap-2.5` на решетката. */
export const CARD_GAP = 10;

/** Ниска карта — името и сумата на един ред (`DesktopTableCard` `oneLine`). */
export const isOneLineCard = (cardHeight: number | null): boolean =>
  cardHeight !== null && cardHeight < CARD_ONE_LINE_BELOW;

/** Решетката с картите (десктоп): N на ред, докато всяка е поне
 *  CARD_MIN_WIDTH — при по-тясна релса колоните намаляват сами (auto-fill);
 *  без избор — колкото се съберат по 150px. Всички карти са ЕДНАКВИ: колоните
 *  са 1fr, а редовете — точно избраната височина (дългото име не разтяга
 *  картите — пренася се, докъдето има място, и се реже с „…"). */
export const cardGridClassOf = ({ cardColumns }: TablesLayout): string =>
  `grid gap-2.5 p-3.5 content-start auto-rows-[var(--card-height,70px)] ${
    cardColumns === null ? "grid-cols-[repeat(auto-fill,minmax(150px,1fr))]" : ""
  }`;

/** Избраните колони/височина са известни чак по време на работа → inline. */
export const cardGridStyleOf = (layout: TablesLayout): CSSProperties =>
  cardSizeStyleOf(layout, CARD_MIN_WIDTH, CARD_GAP);

const clampOrNull = (value: unknown, min: number, max: number): number | null =>
  typeof value === "number" && Number.isFinite(value)
    ? Math.max(min, Math.min(max, Math.round(value)))
    : null;

const store = createLayoutStore<TablesLayout>({
  storagePrefix: "ordering_tables_layout",
  defaults: TABLES_LAYOUT_DEFAULT,
  normalize: (value) => ({
    railWidth: clampOrNull(value?.railWidth, RAIL_MIN_WIDTH, 4000),
    cardColumns: clampOrNull(value?.cardColumns, 1, CARD_MAX_COLUMNS),
    cardHeight: clampOrNull(value?.cardHeight, CARD_HEIGHT_MIN, CARD_HEIGHT_MAX),
  }),
});

/**
 * Сменя размерите. По време на влаченето на релсата — само на екрана
 * (`persist: false`, за да не пише в localStorage на всяко мръдване); при
 * пускане — и на компютъра.
 */
export const updateTablesLayout = store.update;

/** Една стойност от размерите — компонентът се прерисува само когато тя се
 *  смени (влаченето на релсата не прерисува картите и обратно). */
export const useTablesLayout = store.useLayout;

// ── предварителен изглед при влачене на дръжка на карта ─────────────────
// Докато се влачи, картите НЕ се местят — върху тях се рисуват сиви
// пунктирани контури на новия размер (`CardLayoutPreview`); при пускане
// картите заемат точно тях.

export const previewTablesLayout = store.preview;

/** При пускане: предварителният изглед става размерът (и на компютъра). */
export const commitTablesLayoutPreview = store.commitPreview;

/** Размерът, който ще стане при пускане; null — не се влачи карта. */
export const useTablesLayoutPreview = (): TablesLayout | null =>
  store.usePreview((pending) => pending);

/** Дръжките на картите с масите (`CardResizeGrips`) и сивите контури при
 *  влачене (`CardLayoutPreview`). */
export const TABLES_CARD_RESIZE: CardResizeSpec = {
  minWidth: CARD_MIN_WIDTH,
  maxColumns: CARD_MAX_COLUMNS,
  gap: CARD_GAP,
  heightMin: CARD_HEIGHT_MIN,
  heightMax: CARD_HEIGHT_MAX,
  preview: previewTablesLayout,
  commitPreview: commitTablesLayoutPreview,
  reset: (patch) => updateTablesLayout(patch, { persist: true }),
  labelKeys: {
    width: "ordering.tablesLayout.resizeCardWidth",
    height: "ordering.tablesLayout.resizeCardHeight",
    both: "ordering.tablesLayout.resizeCard",
  },
  // По целия десен/долен ръб — по средата на процепа между картите (10px) и
  // малко върху ръба им.
  gripPlacement: {
    width: "inset-y-0 -right-3 w-3.5 cursor-col-resize",
    height: "inset-x-0 -bottom-3 h-3.5 cursor-row-resize",
    both: "-right-3 -bottom-3 size-3.5 cursor-nwse-resize",
  },
  previewContentClassName: "[&_button]:border-transparent [&_button]:bg-transparent",
  previewContourClassName: "rounded-[14px] @max-[112px]/card:rounded-lg",
};
